// src/components/ui/Charts/LineChart.tsx
import React, { useMemo, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Line, LineChart as RechartsLineChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { COLORS } from '@/theme/colors';
import { CustomTooltip } from './CustomTooltip';
import { ChartRenderer } from './ChartRenderer';
import { DATE_FORMATS, LOCALES } from '@/utils/constants';
import { formatDateTime, utcToLocal, formatSmartDate } from '@/utils/dateUtils';

export interface LineDataSeries {
  name: string;
  dataKey: string;
  color?: string;
  dashed?: boolean;
  strokeWidth?: number;
  yAxisId?: string;
  hidden?: boolean; // Added hidden property
}

export type PeriodType = 'day' | 'week' | 'month' | 'quarter' | 'year';

export interface FutureDataConfig {
  periodType: PeriodType;
  nameFormat?: 'numeric' | 'localized' | 'iso'; // добавлен вариант 'iso' для ISO дат
}

export interface LineChartProps {
  // Основные данные и настройки
  data: Array<Record<string, any>>;
  series: LineDataSeries[];
  xAxisKey?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  
  // Базовые настройки отображения (обратная совместимость)
  showGrid?: boolean;
  showLegend?: boolean;
  height?: number;
  className?: string;
  
  // Специфичные настройки для LineChart
  dualAxis?: boolean;
  leftAxisLabel?: string;
  rightAxisLabel?: string;
  curve?: 'linear' | 'monotone' | 'step' | 'stepBefore' | 'stepAfter' | 'basis';
  
  // Настройки данных
  valueType?: 'number' | 'percent' | 'currency' | 'compact';
  precision?: number;
  tooltipFormatter?: (value: any, name: string, props: any) => React.ReactNode;
  
  // Настройки отображения будущих данных
  hideFutureData?: boolean;
  futureDataConfig?: FutureDataConfig;
  
  // Настройки форматирования дат
  xAxisDateFormat?: boolean;
  xAxisDateFormatType?: 'short' | 'full' | 'smart';
  dateFromUTC?: boolean;
  locale?: string;
  
  // Настройки осей и сетки
  xAxisTickLine?: boolean;
  yAxisTickLine?: boolean;
  
  // Новые параметры для унифицированного управления
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'auto';
  minHeight?: number;
  aspectRatio?: number;
  isLoading?: boolean;
  error?: string | null;
  noData?: boolean;
  title?: string;
  subtitle?: string;
  margin?: { top?: number; right?: number; bottom?: number; left?: number };
  
  // Колбэки и обработчики
  onSeriesToggle?: (dataKey: string, isActive: boolean) => void;
  onClick?: (data: any, index: number) => void;
}

/**
 * Компонент для отображения линейных графиков (LineChart)
 * Поддерживает унифицированное управление размерами и состояниями через ChartRenderer
 */
export const LineChart = forwardRef<HTMLDivElement, LineChartProps>(({
  // Основные данные и настройки
  data,
  series,
  xAxisKey = 'name',
  xAxisLabel,
  yAxisLabel,
  
  // Базовые настройки отображения (обратная совместимость)
  showGrid = true,
  showLegend = true,
  height,
  className = '',
  
  // Специфичные настройки для LineChart
  dualAxis = false,
  leftAxisLabel,
  rightAxisLabel,
  curve = 'monotone',
  
  // Настройки данных
  valueType = 'number',
  precision = 2,
  tooltipFormatter,
  
  // Настройки форматирования дат
  xAxisDateFormat = false,
  xAxisDateFormatType = 'short',
  dateFromUTC = true,
  locale = LOCALES.DEFAULT,
  
  // Настройки осей и сетки
  xAxisTickLine = true,
  yAxisTickLine = true,
  
  // Новые параметры для унифицированного управления
  size = 'md',
  minHeight,
  aspectRatio,
  isLoading = false,
  error = null,
  noData = false,
  title,
  subtitle,
  margin = { top: 20, right: 30, bottom: 25, left: 30 },
  
  // Колбэки и обработчики
  onSeriesToggle,
  onClick
}, ref) => {
  const { i18n } = useTranslation(['analytics', 'ui', 'common']);
  const currentLocale = i18n.language || locale;
  
  // Обратная совместимость для размеров
  const effectiveMinHeight = useMemo(() => {
    if (minHeight) return minHeight;
    if (height) return height;
    return undefined;
  }, [minHeight, height]);
  
  // Создаем палитру цветов на основе темы
  const defaultColors = [
    COLORS.primary,
    '#32a852',
    '#3273a8',
    '#a83232',
    '#a87832',
    '#8432a8',
  ];
  
  // Функция форматирования меток оси X для дат
  const formatXAxisTick = useMemo(() => {
    if (!xAxisDateFormat) return undefined;
    
    return (value: string): string => {
      if (!value) return value;
      
      try {
        if (dateFromUTC) {
          switch (xAxisDateFormatType) {
            case 'smart':
              return formatSmartDate(value, false, currentLocale);
            case 'full':
              return formatDateTime(value, false, currentLocale);
            case 'short':
            default:
              // Форматируем только день и месяц
              const localDateTime = utcToLocal(value, currentLocale);
              if (!localDateTime) return value;
              return localDateTime.toFormat(DATE_FORMATS.SHORT_DATE);
          }
        } else {
          // Для дат не в UTC формате применяем стандартное форматирование
          return formatDateTime(value, false, currentLocale);
        }
      } catch (error) {
        console.error('Ошибка форматирования метки оси X:', error);
        return value;
      }
    };
  }, [xAxisDateFormat, xAxisDateFormatType, dateFromUTC, currentLocale]);
  
  // Функция форматирования подсказок с датами
  const formatTooltipLabel = useMemo(() => {
    if (!xAxisDateFormat) return undefined;
    
    return (label: string): string => {
      if (!label) return label;
      
      try {
        if (dateFromUTC) {
          // Используем умное форматирование для подсказок
          return formatSmartDate(label, true, currentLocale);
        } else {
          // Для дат не в UTC формате используем полное форматирование с временем
          return formatDateTime(label, true, currentLocale);
        }
      } catch (error) {
        console.error('Ошибка форматирования метки подсказки:', error);
        return label;
      }
    };
  }, [xAxisDateFormat, dateFromUTC, currentLocale]);
  
  // Обработка видимых/скрытых серий
  const visibleSeries = useMemo(() => 
    series.filter(serie => serie.hidden !== true),
  [series]);
  
  // Определяем, нет ли данных
  const hasNoData = useMemo(() => {
    return noData || !data || data.length === 0 || visibleSeries.length === 0;
  }, [noData, data, visibleSeries]);
  
  // Обработка клика на легенду
  const handleLegendClick = useMemo(() => {
    if (!onSeriesToggle) return undefined;
    return (entry: any) => {
      onSeriesToggle(entry.dataKey, !entry.inactive);
    };
  }, [onSeriesToggle]);
  
  return (
    <ChartRenderer
      size={size}
      minHeight={effectiveMinHeight}
      aspectRatio={aspectRatio}
      className={className}
      isLoading={isLoading}
      error={error}
      noData={hasNoData}
      title={title}
      subtitle={subtitle}
      ref={ref}
    >
      <ResponsiveContainer width="100%" height="100%">
        <RechartsLineChart
          data={data}
          margin={margin}
          onClick={onClick}
        >
          {/* Сетка */}
          {showGrid && (
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke={COLORS.secondary}
              horizontal={true}
              vertical={true}
            />
          )}
          
          {/* Ось X */}
          <XAxis 
            dataKey={xAxisKey} 
            tick={{ fill: COLORS.text, fontSize: 12 }} 
            tickFormatter={formatXAxisTick}
            tickLine={xAxisTickLine}
            axisLine={{ strokeWidth: 1 }}
            padding={{ left: 10, right: 10 }}
            label={xAxisLabel ? { 
              value: xAxisLabel, 
              position: 'insideBottom', 
              offset: -5,
              style: { textAnchor: 'middle' }
            } : undefined}
          />
          
          {/* Ось Y для левой стороны */}
          <YAxis 
            yAxisId="left"
            tick={{ fill: COLORS.text, fontSize: 12 }} 
            tickLine={yAxisTickLine}
            axisLine={{ strokeWidth: 1 }}
            padding={{ top: 10, bottom: 10 }}
            label={dualAxis ? 
              (leftAxisLabel ? { 
                value: leftAxisLabel, 
                angle: -90, 
                position: 'insideLeft',
                style: { textAnchor: 'middle' }
              } : undefined) : 
              (yAxisLabel ? { 
                value: yAxisLabel, 
                angle: -90, 
                position: 'insideLeft',
                style: { textAnchor: 'middle' }
              } : undefined)
            }
          />
          
          {/* Дополнительная ось Y для правой стороны при dualAxis=true */}
          {dualAxis && (
            <YAxis 
              yAxisId="right" 
              orientation="right" 
              tick={{ fill: COLORS.text, fontSize: 12 }} 
              tickLine={yAxisTickLine}
              axisLine={{ strokeWidth: 1 }}
              padding={{ top: 10, bottom: 10 }}
              label={rightAxisLabel ? { 
                value: rightAxisLabel, 
                angle: 90, 
                position: 'insideRight',
                style: { textAnchor: 'middle' }
              } : undefined}
            />
          )}
          
          {/* Тултип */}
          <Tooltip
            content={
              <CustomTooltip 
                valueType={valueType} 
                precision={precision} 
                formatter={tooltipFormatter}
                labelFormatter={formatTooltipLabel}
              />
            }
          />
          
          {/* Легенда */}
          {showLegend && (
            <Legend 
              onClick={handleLegendClick}
              verticalAlign="top"
              wrapperStyle={{ paddingBottom: '10px' }}
            />
          )}
          
          {/* Линии для каждой серии */}
          {visibleSeries.map((serie, index) => {
            const color = serie.color || defaultColors[index % defaultColors.length];
            return (
              <Line
                key={serie.dataKey}
                type={curve}
                dataKey={serie.dataKey}
                name={serie.name}
                stroke={color}
                strokeWidth={serie.strokeWidth || 2}
                strokeDasharray={serie.dashed ? '5 5' : undefined}
                yAxisId={serie.yAxisId || 'left'}
                dot={{ r: 3, strokeWidth: 1, fill: '#fff' }}
                activeDot={{ r: 5, strokeWidth: 1 }}
                connectNulls={false} // Не соединять линией точки с null значениями
              />
            );
          })}
        </RechartsLineChart>
      </ResponsiveContainer>
    </ChartRenderer>
  );
});

LineChart.displayName = 'LineChart';

export default LineChart;